import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  ArchiveReasonDocument,
  ClearDefaultReasonDocument,
  connectionRows,
  CreateReasonDocument,
  DeleteReasonDocument,
  GetReasonsDocument,
  type ReasonRow,
  SetDefaultReasonDocument,
  UnarchiveReasonDocument,
  UpdateReasonDocument,
} from "#app/lib/data";
import { addReasonToCache, removeReasonFromCache } from "./reason-cache-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { OutcomeTone } from "#app/lib/outcome-tone";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";

const trackOutcome = (
  event: (typeof SettingsEvent)[keyof typeof SettingsEvent],
  data?: Record<string, unknown>,
) => trackEvent({ context: AnalyticsContext.Settings, event, label: "outcome", ...(data ? { data } : {}) });

export type Reason = ReasonRow;

// `Reason` is the server's noun for a Project Update outcome. The toasts and
// the settings surface say "outcome" — the schema is additive-only, so the
// stored name stays while the vocabulary moved on.

const REASONS_QUERY_VARS = { limit: 50, orderBy: { name: 1 } };

export function useReasons() {
  const { data, loading, error } = useQuery(GetReasonsDocument, {
    variables: REASONS_QUERY_VARS,
    fetchPolicy: "cache-and-network",
  });

  const reasons = useMemo<Reason[]>(
    () => connectionRows(data?.reasons),
    [data],
  );

  return {
    reasons,
    isLoading: loading,
    error,
  };
}

export function useReasonMutations() {
  const [createMutation] = useMutation(CreateReasonDocument);
  const [updateMutation] = useMutation(UpdateReasonDocument);
  const [archiveMutation] = useMutation(ArchiveReasonDocument);
  const [unarchiveMutation] = useMutation(UnarchiveReasonDocument);
  const [deleteMutation] = useMutation(DeleteReasonDocument);
  const [setDefaultMutation] = useMutation(SetDefaultReasonDocument);
  const [clearDefaultMutation] = useMutation(ClearDefaultReasonDocument);

  const createReason = useCallback(
    async (name: string, tone: OutcomeTone) => {
      try {
        const { data } = await createMutation({
          variables: { input: { name, tone } },
          update(cache, { data }) {
            const result = data?.createReason;
            if (result?.__typename !== "Reason") return;
            addReasonToCache(cache, result);
          },
        });
        const result = data?.createReason;
        if (result?.__typename === "Reason") {
          trackOutcome(SettingsEvent.Create, { tone });
          return result;
        }
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to create outcome", type: "error" });
          return null;
        }
        toastManager.add({ title: "Failed to create outcome", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create outcome");
        return null;
      }
    },
    [createMutation],
  );

  const updateReason = useCallback(
    async (id: string, name: string, tone: OutcomeTone) => {
      try {
        const { data } = await updateMutation({
          variables: { id, input: { name, tone } },
        });
        const result = data?.updateReason;
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to update outcome", type: "error" });
          return null;
        }
        if (result?.__typename === "Reason") {
          trackOutcome(SettingsEvent.Update, { tone });
          return result;
        }
        toastManager.add({ title: "Failed to update outcome", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update outcome");
        return null;
      }
    },
    [updateMutation],
  );

  const toggleReason = useCallback(
    async (reason: Reason) => {
      try {
        if (reason.archived) {
          await unarchiveMutation({ variables: { id: reason.id } });
        } else {
          await archiveMutation({ variables: { id: reason.id } });
        }
        trackOutcome(reason.archived ? SettingsEvent.Unarchive : SettingsEvent.Archive);
      } catch (err) {
        toastMutationError(err, "Failed to update outcome");
      }
    },
    [archiveMutation, unarchiveMutation],
  );

  const deleteReason = useCallback(
    async (reason: Reason) => {
      try {
        await deleteMutation({
          variables: { id: reason.id },
          update(cache, { data }) {
            const result = data?.deleteReason;
            if (result?.__typename !== "Reason") return;
            removeReasonFromCache(cache, result);
          },
        });
        trackOutcome(SettingsEvent.Delete);
      } catch (err) {
        toastMutationError(err, "Failed to delete outcome");
      }
    },
    [deleteMutation],
  );

  const setDefaultReason = useCallback(
    async (id: string) => {
      try {
        await setDefaultMutation({
          variables: { id },
          refetchQueries: ["GetReasons"],
        });
        trackOutcome(SettingsEvent.SetDefault, { cleared: false });
      } catch (err) {
        toastMutationError(err, "Failed to update default outcome");
      }
    },
    [setDefaultMutation],
  );

  const clearDefaultReason = useCallback(
    async () => {
      try {
        await clearDefaultMutation({
          refetchQueries: ["GetReasons"],
        });
        trackOutcome(SettingsEvent.SetDefault, { cleared: true });
      } catch (err) {
        toastMutationError(err, "Failed to clear default outcome");
      }
    },
    [clearDefaultMutation],
  );

  return {
    createReason,
    updateReason,
    toggleReason,
    deleteReason,
    setDefaultReason,
    clearDefaultReason,
  };
}
